/**
 * @file BookedOverBadge.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-11-03
 * @modified 2026-11-03
 * @description The order window's "Booked over" pill (Cam, 3 Nov 2026 — "record
 *              the book over on the order as we go"): the order's invoice was
 *              moved to another date, or split over several invoices
 *              (Pricing & Costing's Invoice window, or Book over… on an
 *              invoice already raised). Each part — its number and date — is a
 *              link that opens that invoice; the note on hover. The order's
 *              History has the BOOKED_OVER entry with what each booking
 *              replaced. Nothing renders for an order not booked over.
 *
 *   Props: { parts: [{ id, invoice_no, invoice_date, due_date, part, of, note }] }
 *   (GET /api/orders/customer-orders/:id `booked_over`), onChanged().
 */

import React from 'react';
import { useEntityModal } from './EntityModalProvider';
import { formatDateShort } from '../utils/dateFormatters';
import './BookedOverBadge.css';

/** "Booked over to 01/11/2026" / "Split into 3 invoices" (pure). */
export function bookedOverPillText(parts) {
  const list = (parts || []).filter(Boolean);
  if (!list.length) return '';
  if (list.length === 1) return `Booked over to ${formatDateShort(list[0].invoice_date)}`;
  return `Split into ${list.length} invoices`;
}

function BookedOverBadge({ parts, onChanged }) {
  const { openEntity, available } = useEntityModal();
  const list = (parts || []).filter(Boolean);
  if (!list.length) return null;
  const note = list.find(p => p.note)?.note || '';
  const canOpen = !!available;
  return (
    <span className="booked-over-badge" data-testid="booked-over-badge"
      title={`${bookedOverPillText(list)}${note ? ` — "${note}"` : ''}. The order's History says what each booking replaced.`}>
      <span className="material-symbols-rounded" aria-hidden="true">event_repeat</span>
      <span>{bookedOverPillText(list)}:</span>
      {list.map((p) => (
        <button key={p.id} type="button" data-read-safe className="booked-over-part"
          disabled={!canOpen}
          title={`Open ${p.invoice_no} — dated ${formatDateShort(p.invoice_date)}, due ${formatDateShort(p.due_date)}${p.of > 1 ? ` (part ${p.part} of ${p.of})` : ''}`}
          onClick={(e) => { e.stopPropagation(); openEntity('invoice', p.id, { onClose: () => onChanged && onChanged() }); }}>
          {p.invoice_no} <small>{formatDateShort(p.invoice_date)}</small>
        </button>
      ))}
    </span>
  );
}

export default BookedOverBadge;
